/** @prettier */

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {SLIUAddressSelectBox} from '../../component/SelectBox';
import SLIUUnitTypeSelectBox from '../sl-iu-registration/SLIUUnitTypeSelectBox';
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  patchCompositionsSLUnit,
  getCompositionsSLUnitByOUID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';
import {format} from '../../define';

/**
 * 室内機編集ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const IUEditDialog = (props: {
  /** 修正する室内機ID */
  iuID: string;
  /** 修正する室内機が登録されている室外機の情報 */
  ouInfo: apiIF.res.getCompositionsSLUnitByOUID | null;
  /** 修正する室内機が登録されている室外機の情報（set関数） */
  setOUInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLUnitByOUID | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  //編集前の室内機情報を取得
  let tempIUName = '';
  let tempIUAddress = '';
  let tempUnitType: apiIF.res.getCompositionsSLUnitByOUID['indoor-unit-informations'][0]['indoor-unit-type'] =
    'FDT';
  if (props.ouInfo !== null) {
    for (const iuInfo of props.ouInfo['indoor-unit-informations']) {
      if (iuInfo['unit-id'] === props.iuID) {
        tempIUName = iuInfo['indoor-unit-name'];
        tempIUAddress = iuInfo['sl-address'];
        tempUnitType = iuInfo['indoor-unit-type'];
      }
    }
  }

  const [iuName, setIUName] = useState(tempIUName); //室内機の名前
  const [iuAddress, setIUAddress] = useState(tempIUAddress); //SLアドレス
  const [unitType, setUnitType] = useState(tempUnitType); //室内機タイプ
  const [okBtnDisabled, setOKBtnDisabled] = useState(false); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時のエラーダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [iuNameError, setIUNameError] = useState(false); //室内機の名前

  //入力項目のチェック
  useEffect(() => {
    if (iuName === '' || iuNameError || apiWait) {
      setOKBtnDisabled(true);
    } else {
      setOKBtnDisabled(false);
    }
  }, [iuName, iuNameError, apiWait]);

  /**
   * OKボタン押下時処理
   * @returns
   */
  const editIU = () => {
    //パスパラメータを取得できない場合
    //室内外機の情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      ouID === undefined ||
      props.ouInfo === null
    ) {
      return;
    }

    setAPIWait(true);

    //APIに送信するデータの作成
    const editData: apiIF.reqParameters.patchCompositionsSLUnit = {
      'indoor-unit-informations': props.ouInfo['indoor-unit-informations'].map(
        iuInfo => {
          if (iuInfo['unit-id'] === props.iuID) {
            const temp = {...iuInfo};
            temp['indoor-unit-name'] = iuName;
            temp['sl-address'] = iuAddress;
            temp['indoor-unit-type'] = unitType;
            return temp;
          }
          return iuInfo;
        }
      ),
    };
    //情報を編集
    patchCompositionsSLUnit(siteID, gwID, ouID, editData)
      .then(() => {
        //室外機の情報を取得
        return getCompositionsSLUnitByOUID(siteID, gwID, ouID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['indoor-unit-informations'].sort(
          sortByUnicode('indoor-unit-name')
        );

        props.setOUInfo(response);
        props.setIsOpenDialog(false);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setIUName(tempIUName);
    setIUAddress(tempIUAddress);
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('IUEditDialog.superlinkUnit.title')}
        </DialogTitle>
        <DialogContent>
          {/* ダイアログに表示するメッセージ */}
          <DialogContentText>
            {trans('IUEditDialog.superlinkUnit.msg')}
          </DialogContentText>
          <Stack spacing={1.5}>
            {/* 室内機名称 */}
            <ChkInputTextbox
              setValue={setIUName}
              setError={setIUNameError}
              chkLength={format.BASIC_LENGTH_MAX}
              type="text"
              label={trans(
                'IUEditDialog.superlinkUnit.txtField.label.unitName'
              )}
              value={iuName}
              id="mv-editIUName"
              class="gwSetting__contents__textBox--unitName"
              helperText=" "
              errHelperText={trans(
                'IUEditDialog.superlinkUnit.txtField.errHelperTxt'
              )}
              variant="standard"
              autoFocus={true}
              margin="dense"
              chkInputProhibitedCharacter={format.BASIC_PATTERN_S3_POLICY}
            />
            {/* SLアドレス */}
            <SLIUAddressSelectBox
              setAddress={setIUAddress}
              label={trans(
                'IUEditDialog.superlinkUnit.txtField.label.superlinkAddress'
              )}
              default={tempIUAddress}
              class="gwSetting__contents__textBox--address"
              id="mv-selectIuUnit"
            />
            {/* 室内機タイプ */}
            <SLIUUnitTypeSelectBox
              setUnitType={setUnitType}
              defaultValue={tempUnitType}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('IUEditDialog.buttonTxt.cancel')}
            id={'mv-btnCancelEditIu'}
          />
          <PrimaryButton
            onClick={editIU}
            buttonText={trans('IUEditDialog.buttonTxt.ok')}
            id={'mv-btnOkEditIu'}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${tempIUName}EditErrDlgBtn`}
      />
    </>
  );
};
export default IUEditDialog;
